import { useCallback, useEffect, useState } from 'react'

export interface GenerationFormData {
  modelSlug: string | undefined
  prompt: string
  width: number
  height: number
  widthInput: string
  heightInput: string
  numImages: number
  steps: number
  cfgScale: number
  clipSkip: number
  seedMode: 'fixed' | 'random'
  seed: number | null
  selectedLoras: Record<number, number>
}

const STORAGE_KEY = 'generation-form-data'

const BASE_DATA: Omit<GenerationFormData, 'modelSlug' | 'selectedLoras'> = {
  prompt: '',
  width: 1024,
  height: 1024,
  widthInput: '1024',
  heightInput: '1024',
  numImages: 4,
  steps: 20,
  cfgScale: 3.5,
  clipSkip: -1,
  seedMode: 'fixed',
  seed: null,
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function toNumber(value: unknown): number | undefined {
  return typeof value === 'number' && !Number.isNaN(value) ? value : undefined
}

function toString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function sanitizeSeedMode(value: unknown): 'fixed' | 'random' | undefined {
  return value === 'random' ? 'random' : value === 'fixed' ? 'fixed' : undefined
}

function sanitizeSelectedLoras(value: unknown): Record<number, number> {
  if (!isRecord(value)) {
    return {}
  }

  const next: Record<number, number> = {}

  for (const [key, strengthValue] of Object.entries(value)) {
    const id = Number(key)
    const strength = toNumber(strengthValue)
    if (!Number.isFinite(id) || Number.isNaN(id) || id <= 0) {
      continue
    }
    if (typeof strength === 'number') {
      next[id] = strength
    }
  }

  return next
}

function sanitizeStoredData(raw: unknown): Partial<GenerationFormData> {
  if (!isRecord(raw)) {
    return {}
  }

  const sanitized: Partial<GenerationFormData> = {}

  const modelSlug = toString(raw.modelSlug)
  if (modelSlug) sanitized.modelSlug = modelSlug

  const prompt = toString(raw.prompt)
  if (prompt !== undefined) sanitized.prompt = prompt

  const width = toNumber(raw.width)
  if (width !== undefined) sanitized.width = width

  const height = toNumber(raw.height)
  if (height !== undefined) sanitized.height = height

  const widthInput = toString(raw.widthInput)
  if (widthInput !== undefined) sanitized.widthInput = widthInput

  const heightInput = toString(raw.heightInput)
  if (heightInput !== undefined) sanitized.heightInput = heightInput

  const numImages = toNumber(raw.numImages)
  if (numImages !== undefined) sanitized.numImages = numImages

  const steps = toNumber(raw.steps)
  if (steps !== undefined) sanitized.steps = steps

  const cfgScale = toNumber(raw.cfgScale)
  if (cfgScale !== undefined) sanitized.cfgScale = cfgScale

  const clipSkip = toNumber(raw.clipSkip)
  if (clipSkip !== undefined) sanitized.clipSkip = clipSkip

  const seedMode = sanitizeSeedMode(raw.seedMode)
  if (seedMode) sanitized.seedMode = seedMode

  const seed = toNumber(raw.seed)
  if (seed !== undefined) sanitized.seed = seed

  const selectedLoras = sanitizeSelectedLoras(raw.selectedLoras)
  if (Object.keys(selectedLoras).length > 0) {
    sanitized.selectedLoras = selectedLoras
  }

  return sanitized
}

export function createGenerationFormBase(): GenerationFormData {
  return {
    modelSlug: undefined,
    ...BASE_DATA,
    selectedLoras: {},
  }
}

export function useGenerationFormPersistence() {
  const [isReady, setIsReady] = useState(false)

  const load = useCallback((): Partial<GenerationFormData> => {
    if (typeof window === 'undefined') {
      return {}
    }

    try {
      const stored = window.localStorage.getItem(STORAGE_KEY)
      if (!stored) return {}
      const parsed = JSON.parse(stored)
      return sanitizeStoredData(parsed)
    } catch (error) {
      console.warn('Failed to read generation form data from localStorage:', error)
      return {}
    }
  }, [])

  const save = useCallback((data: GenerationFormData) => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch (error) {
      console.warn('Failed to persist generation form data to localStorage:', error)
    }
  }, [])

  const clear = useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.warn('Failed to clear generation form data from localStorage:', error)
    }
  }, [])

  useEffect(() => {
    setIsReady(true)
  }, [])

  return {
    isReady,
    load,
    save,
    clear,
  }
}
